import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Box, Flex, Skeleton, Table, Text } from '@radix-ui/themes';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTooltip } from 'components/common/tooltip';
import { RemoteCalibrationStream } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/remote-calibration-stream';
import useVisionRemoteStore from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/store/useVisionRemoteStore';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import {
  feetFloatToString,
  MM_TO_FT,
  round,
} from 'lib_ts/classes/math.utilities';
import { LOCAL_VERBOSE_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { VisionCalibrationCameraType } from 'lib_ts/enums/vision.enums';
import { isNil } from 'lodash';
import { CSSProperties, PropsWithChildren } from 'react';

const ColumnHeaderCell = (
  props: PropsWithChildren<{ style?: CSSProperties }>
) => (
  <Table.ColumnHeaderCell width="calc(100% / 3)" style={props.style}>
    {props.children}
  </Table.ColumnHeaderCell>
);

const MeasurementCell = (props: {
  value?: string | number;
  unit?: string;
  style?: CSSProperties;
}) => {
  if (isNil(props.value)) {
    return <Table.Cell />;
  }

  return (
    <Table.Cell style={props.style}>
      <Flex gap={RADIX.FLEX.GAP.XS} align="end">
        <Text>
          {typeof props.value === 'string'
            ? props.value
            : round(props.value, 2)}
        </Text>
        {props.unit && (
          <Text size="1" color={RADIX.COLOR.HINT}>
            {props.unit}
          </Text>
        )}
      </Flex>
    </Table.Cell>
  );
};

const DataTableContainer = (props: PropsWithChildren) => (
  <Box
    style={{
      borderRadius: 'var(--radius-4)',
      border: 'solid 1px var(--slate-6)',
      borderWidth: '1px 1px 0 1px',
    }}
  >
    {props.children}
  </Box>
);

export const RemoteVisionCalibrationOverview = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const lastResponse = useVisionRemoteStore((s) => s.lastResponse);
  const loading = useVisionRemoteStore((s) => s.loading);

  if (!machine) {
    return null;
  }

  // using this instead of MachineHelper.getDistanceOptionValue in case vision provides a value thats beyond our typical min/max
  // plate distance as reported by vision
  const vPlateDistanceFt = feetFloatToString(lastResponse?.plate_distance_ft);

  // plate distance as recorded on machine record
  const mPlateDistanceFt = feetFloatToString(
    machine.releaseFt - MachineHelper.getWheelsInsetFt(machine.hardware_version)
  );

  return (
    <ErrorBoundary identifier="RemoteVisionCalibrationOverview">
      <Flex direction="column" gap="4" height="100%" overflow="hidden">
        <Flex direction="column" gap="4" overflow="auto">
          <Flex gap={RADIX.FLEX.GAP.SM}>
            <Flex gap={RADIX.FLEX.GAP.SM} direction="column" flexGrow="1">
              <Text size="2" weight="medium">
                Leader Camera
              </Text>
              <RemoteCalibrationStream
                cameraType={VisionCalibrationCameraType.leader}
                hideStreamFailed
              />
            </Flex>
            <Flex gap={RADIX.FLEX.GAP.SM} direction="column" flexGrow="1">
              <Text size="2" weight="medium">
                Follower Camera
              </Text>
              <RemoteCalibrationStream
                cameraType={VisionCalibrationCameraType.follower}
                hideStreamFailed
              />
            </Flex>
          </Flex>
          <Skeleton loading={!lastResponse}>
            <DataTableContainer>
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <ColumnHeaderCell
                      style={{ borderTopLeftRadius: 'var(--radius-4)' }}
                    >
                      Measurement Type
                    </ColumnHeaderCell>
                    <ColumnHeaderCell>Distance</ColumnHeaderCell>
                    <ColumnHeaderCell
                      style={{ borderTopRightRadius: 'var(--radius-4)' }}
                    >
                      Last Measured
                    </ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>
                      <Flex align="center" gap={RADIX.FLEX.GAP.XS}>
                        <Text>{t('settings.plate-distance')}</Text>
                        <CommonTooltip
                          text_md="settings.tape-measured-distance-msg"
                          trigger={<InfoCircledIcon />}
                        />
                      </Flex>
                    </Table.Cell>
                    <MeasurementCell value={mPlateDistanceFt} unit="ft" />
                    <Table.Cell
                      title={
                        machine.last_plate_distance_changedBy
                          ? `Changed by ${machine.last_plate_distance_changedBy}`
                          : undefined
                      }
                    >
                      {machine.last_plate_distance_changed &&
                        format(
                          parseISO(machine.last_plate_distance_changed),
                          LOCAL_VERBOSE_DATETIME_FORMAT
                        )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell
                      style={{ borderBottomLeftRadius: 'var(--radius-4)' }}
                    >
                      Vision Plate Distance
                    </Table.Cell>
                    <MeasurementCell value={vPlateDistanceFt} unit="ft" />
                    <Table.Cell
                      style={{ borderBottomRightRadius: 'var(--radius-4)' }}
                    >
                      {vPlateDistanceFt && 'Now'}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </DataTableContainer>
          </Skeleton>
          <Skeleton loading={!lastResponse}>
            <DataTableContainer>
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <ColumnHeaderCell
                      style={{ borderTopLeftRadius: 'var(--radius-4)' }}
                    >
                      Parameter
                    </ColumnHeaderCell>
                    <ColumnHeaderCell>Leader Camera</ColumnHeaderCell>
                    <ColumnHeaderCell
                      style={{ borderTopRightRadius: 'var(--radius-4)' }}
                    >
                      Follower Camera
                    </ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>Position X</Table.Cell>
                    <MeasurementCell
                      value={
                        isNil(lastResponse?.leader.x_mm)
                          ? undefined
                          : feetFloatToString(
                              (lastResponse?.leader.x_mm ?? 0) * MM_TO_FT
                            )
                      }
                      unit="ft"
                    />
                    <MeasurementCell
                      value={
                        isNil(lastResponse?.follower.x_mm)
                          ? undefined
                          : feetFloatToString(
                              (lastResponse?.follower.x_mm ?? 0) * MM_TO_FT
                            )
                      }
                      unit="ft"
                    />
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Position Y</Table.Cell>
                    <MeasurementCell
                      value={
                        isNil(lastResponse?.leader.y_mm)
                          ? undefined
                          : feetFloatToString(
                              (lastResponse?.leader.y_mm ?? 0) * MM_TO_FT
                            )
                      }
                      unit="ft"
                    />
                    <MeasurementCell
                      value={
                        isNil(lastResponse?.follower.y_mm)
                          ? undefined
                          : feetFloatToString(
                              (lastResponse?.follower.y_mm ?? 0) * MM_TO_FT
                            )
                      }
                      unit="ft"
                    />
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Position Z</Table.Cell>
                    <MeasurementCell
                      value={
                        isNil(lastResponse?.leader.z_mm)
                          ? undefined
                          : feetFloatToString(
                              (lastResponse?.leader.z_mm ?? 0) * MM_TO_FT
                            )
                      }
                      unit="ft"
                    />
                    <MeasurementCell
                      value={
                        isNil(lastResponse?.follower.z_mm)
                          ? undefined
                          : feetFloatToString(
                              (lastResponse?.follower.z_mm ?? 0) * MM_TO_FT
                            )
                      }
                      unit="ft"
                    />
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Roll</Table.Cell>
                    <MeasurementCell
                      value={lastResponse?.leader.roll_deg}
                      unit="deg"
                    />
                    <MeasurementCell
                      value={lastResponse?.follower.roll_deg}
                      unit="deg"
                    />
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Pitch</Table.Cell>
                    <MeasurementCell
                      value={lastResponse?.leader.pitch_deg}
                      unit="deg"
                    />
                    <MeasurementCell
                      value={lastResponse?.follower.pitch_deg}
                      unit="deg"
                    />
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell
                      style={{ borderBottomLeftRadius: 'var(--radius-4)' }}
                    >
                      Yaw
                    </Table.Cell>
                    <MeasurementCell
                      value={lastResponse?.leader.yaw_deg}
                      unit="deg"
                    />
                    <MeasurementCell
                      style={{ borderBottomRightRadius: 'var(--radius-4)' }}
                      value={lastResponse?.follower.yaw_deg}
                      unit="deg"
                    />
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </DataTableContainer>
          </Skeleton>
        </Flex>
        <Box ml="auto">
          <DialogButton
            label="Start Remote Calibration"
            color={RADIX.COLOR.ACCENT}
            loading={loading || !lastResponse}
            onClick={useVisionRemoteStore.getState().nextStage}
            variant="solid"
          />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
